// The wizards' GENERAL DETAILS card — one builder per view model; see the slice CLAUDE.md.
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { formatQuantityOrDash } from '@/lib/format';
import { EMPTY_LIST } from '@/lib/utils';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
  rowUnitSymbol,
} from '@/lib/mappers/quantities';
import { resolveRowUnitType } from '@/hooks';

/**
 * The card describes ONE side of the link while the dropdown drives the table of the other,
 * so the chosen basis is not always one this side can express — a contract has no tracking
 * unit, and the header reads `mixed` while the rows disagree. Both fall back to this side's
 * own basis rather than to an empty set, which rendered the whole card as dashes.
 */
const chosenUnitType = (viewModel, unitType) =>
  unitType && viewModel?.[`${unitType}Unit`] ? unitType : UNIT_TYPES.declaration;

const rowUnitTypeOf = (row, unitType) => (row?.byUnit?.[unitType] ? unitType : row?.unitType);

// No unit type asked for means the row's own — what the order cards, which have no header, read.
const rowQuantitiesInUnit = (row, unitType) => row?.byUnit?.[rowUnitTypeOf(row, unitType)] ?? {};

/**
 * What step 2 has typed so far, in ONE basis — the modal wraps this as the `assignedInUnit`
 * every builder below is handed. Rows reading in another unit are skipped rather than
 * summed: converting between bases is `POST /units/convert`'s job, never arithmetic here.
 */
export const assignedTotalInUnit = (rows, assignments, selections, unitType) =>
  (rows ?? []).reduce((total, row) => {
    if (resolveRowUnitType(row, selections) !== unitType) return total;
    const amount = Number(assignments?.[row.id]);
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);

/** The card's "what is left" row shrinks live as step 2 is filled in; the raw base allows it. */
const remainingAfterAssigned = (formatted, raw, unit, assigned) =>
  assigned && raw != null ? formatQuantityOrDash(raw - assigned, unit) : (formatted ?? '-');

/** Every row agrees, or the card says nothing — a first row's value is not the group's. */
const sharedValue = (rows, key) => {
  const first = rows[0]?.[key];
  return rows.every((row) => row?.[key] === first) ? first : undefined;
};

/**
 * One row standing for a whole bulk selection, so the card reads the same as it does for a
 * single row. A unit type is summed only where EVERY row carries it in the SAME unit —
 * otherwise the total would silently omit a row, or add two different units together.
 */
export const aggregateLinkRows = (rows = EMPTY_LIST, identityKeys = EMPTY_LIST) => {
  if (rows.length === 0) return null;

  const unitsByType = {};
  for (const unitType of Object.keys(rows[0].rawByUnit ?? {})) {
    const unit = rows[0].byUnit?.[unitType]?.unit;
    const shared = rows.every((row) => row.byUnit?.[unitType]?.unit === unit && unit);
    if (!shared) continue;
    const amounts = {};
    for (const field of Object.keys(rows[0].rawByUnit[unitType])) {
      const values = rows.map((row) => row.rawByUnit?.[unitType]?.[field]);
      amounts[field] = values.some((value) => value == null)
        ? null
        : values.reduce((total, value) => total + value, 0);
    }
    unitsByType[unitType] = { unit, amounts };
  }

  const byUnit = buildQuantitiesByUnit(unitsByType);
  const identity = Object.fromEntries(identityKeys.map((key) => [key, sharedValue(rows, key)]));
  return {
    ...identity,
    byUnit,
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    unitType: defaultUnitType(byUnit),
  };
};

const shipmentIdentityFields = (t, shipment) => [
  { label: t('FIELD_MATERIAL_NAME'), value: shipment.materialName },
  { label: t('FIELD_SENT_BY'), value: shipment.supplierName },
  { label: t('FIELD_RECEIVED_AT'), value: shipment.facilityName },
  { label: t('FIELD_TERMINAL'), value: shipment.terminal },
];

const shipmentLinkCountFields = (t, shipment) => [
  { label: t('FIELD_DATE_OF_DISPATCH'), value: shipment.dateOfDispatch },
  { label: t('FIELD_DATE_OF_DISCHARGE'), value: shipment.dateOfDischarge },
  {
    label: t('FIELD_TOTAL_CONTRACTS_LINKED'),
    value: String(shipment.totalContractsLinked ?? '-'),
  },
  {
    label: t('INCOMING_SHIPMENTS_COL_TOTAL_DECLARATIONS_RECEIVED'),
    value: String(shipment.totalDeclarationsReceived ?? '-'),
  },
];

const shipmentQuantityFields = (t, { shipmentQuantity, unassignedQuantity, receivedQuantity }) => [
  { label: t('FIELD_SHIPMENT_QUANTITY'), value: shipmentQuantity ?? '-' },
  { label: t('FIELD_UNASSIGNED_QUANTITY'), value: unassignedQuantity ?? '-' },
  {
    label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
    value: receivedQuantity ?? '-',
  },
];

/** Link contracts, from a shipments-list row — quantities in the wizard's chosen unit. */
export const buildShipmentLinkDetailFields = (t, row, unitType, assignedInUnit) => {
  if (!row) return [];
  const chosen = rowUnitTypeOf(row, unitType);
  const unit = row.byUnit?.[chosen] ?? {};
  return [
    ...shipmentIdentityFields(t, row),
    ...shipmentQuantityFields(t, {
      shipmentQuantity: unit.shipmentQuantity,
      unassignedQuantity: remainingAfterAssigned(
        unit.unassignedQuantity,
        row.rawByUnit?.[chosen]?.unassignedQuantity,
        unit.unit,
        assignedInUnit?.(chosen) ?? 0,
      ),
      receivedQuantity: unit.receivedDeclarationQuantity,
    }),
    ...shipmentLinkCountFields(t, row),
  ];
};

/** Link contracts, from the shipment details view model, which holds one set per unit. */
export const buildShipmentDetailLinkFields = (t, shipment, unitType, assignedInUnit) => {
  if (!shipment) return [];
  const chosen = chosenUnitType(shipment, unitType);
  const quantities = shipment[`${chosen}Quantities`] ?? {};
  const raw = shipment.rawQuantities?.[chosen];
  return [
    ...shipmentIdentityFields(t, shipment),
    ...shipmentQuantityFields(t, {
      shipmentQuantity: quantities.shipmentQuantity,
      unassignedQuantity: remainingAfterAssigned(
        raw?.unassignedFormatted,
        raw?.unassigned,
        raw?.unit,
        assignedInUnit?.(chosen) ?? 0,
      ),
      receivedQuantity: quantities.receivedDeclarationQuantity,
    }),
    ...shipmentLinkCountFields(t, shipment),
  ];
};

/** Link shipments, from the declaration details view model — quantities in the chosen unit. */
export const buildDeclarationLinkDetailFields = (t, declaration, unitType) => {
  if (!declaration) return [];
  const unit = rowQuantitiesInUnit(declaration, unitType);
  return [
    { label: t('FIELD_MATERIAL_NAME'), value: declaration.materialName ?? '-' },
    { label: t('FIELD_SENT_BY'), value: declaration.supplierName ?? '-' },
    { label: t('FIELD_RECEIVED_AT'), value: declaration.facilityName ?? '-' },
    { label: t('FIELD_TERMINAL'), value: declaration.terminal ?? '-' },
    { label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'), value: unit.receivedQty ?? '-' },
    { label: t('DECLARATIONS_COL_ADJUSTED_QTY'), value: unit.adjustedQty ?? '-' },
    { label: t('FIELD_AVAILABLE_QUANTITY'), value: unit.availableQty ?? '-' },
  ];
};

/** Bulk step 2's per-shipment panel, from an order — the mirror of buildOrderPanelFields. */
export const buildShipmentPanelFields = (t, shipment, unitType, assignedInUnit) => {
  if (!shipment) return [];
  const chosen = rowUnitTypeOf(shipment, unitType);
  const unit = shipment.byUnit?.[chosen] ?? {};
  return [
    { label: t('FIELD_SHIPMENT_ID'), value: shipment.shipmentId ?? '-' },
    { label: t('FIELD_TOTAL_ORDER_LINKED'), value: shipment.linkedOrders ?? '-' },
    { label: t('FIELD_MATERIAL_NAME'), value: shipment.materialName ?? '-' },
    { label: t('FIELD_SITE'), value: shipment.facilityName ?? '-' },
    { label: t('FIELD_TERMINAL'), value: shipment.terminal ?? '-' },
    { label: t('FIELD_SOLD_TO'), value: shipment.soldTo ?? '-' },
    { label: t('FIELD_SHIPPING_SITES'), value: shipment.shippingTo ?? '-' },
    { label: t('FIELD_SHIPMENT_QUANTITY'), value: unit.shipmentQuantity ?? '-' },
    { label: t('FIELD_ALLOCATED_QUANTITY'), value: unit.allocatedQuantity ?? '-' },
    {
      label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
      value: unit.remainingToAllocateQuantity ?? '-',
    },
    {
      label: t('FIELD_UNASSIGNED_QUANTITY'),
      value: remainingAfterAssigned(
        unit.unassignedQuantity,
        shipment.unassignedByUnit?.[chosen],
        rowUnitSymbol(shipment, chosen),
        assignedInUnit?.(chosen) ?? 0,
      ),
    },
  ];
};

// Bulk step 2's per-order panel. TODO: add "Total outgoing shipments linked" once PendingOrder exposes a count.
export const buildOrderPanelFields = (t, order, unitType, assignedInUnit) => {
  if (!order) return [];
  const chosen = rowUnitTypeOf(order, unitType);
  const unit = order.byUnit?.[chosen] ?? {};
  return [
    { label: t('ORDERS_COL_ORDER_NO'), value: order.orderNo ?? '-' },
    {
      label: t('LINK_ORDERS_COL_TOTAL_SHIPMENTS_LINKED'),
      value: order.totalShipmentsLinked ?? '-',
    },
    { label: t('FIELD_SOLD_TO'), value: order.soldTo ?? '-' },
    { label: t('FIELD_SHIPPING_SITES'), value: order.shippingTo ?? '-' },
    { label: t('FIELD_CONTRACTED_QUANTITY'), value: unit.contractedQuantity ?? '-' },
    { label: t('FIELD_SHIPPED_QUANTITY'), value: unit.shippedQuantity ?? '-' },
    { label: t('ORDERS_COL_REMAINING_TO_SHIP'), value: unit.remainingToShipQuantity ?? '-' },
    { label: t('FIELD_ALLOCATED_QUANTITY'), value: unit.allocatedQuantity ?? '-' },
    {
      label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
      value: unit.remainingToAllocateQuantity ?? '-',
    },
    {
      label: t('FIELD_UNASSIGNED_QUANTITY'),
      value: remainingAfterAssigned(
        unit.unassignedQuantity,
        order.unassignedByUnit?.[chosen],
        rowUnitSymbol(order, chosen),
        assignedInUnit?.(chosen) ?? 0,
      ),
    },
  ];
};

const contractIdentityFields = (t, identity, contract) => [
  { label: t('FIELD_MATERIAL_NAME'), value: identity.materialName ?? '-' },
  { label: t('FIELD_SENT_BY'), value: identity.supplierName ?? '-' },
  { label: t('FIELD_RECEIVED_AT'), value: identity.facilityName ?? '-' },
  { label: t('FIELD_TERMINAL'), value: contract.terminal ?? '-' },
];

const contractQuantityFields = (t, deliverBy, { contracted, shipped, outstanding }) => [
  { label: t('FIELD_DELIVER_BY'), value: deliverBy ?? '-' },
  { label: t('FIELD_CONTRACTED_QUANTITY'), value: contracted ?? '-' },
  { label: t('FIELD_SHIPPED_QUANTITY'), value: shipped ?? '-' },
  { label: t('FIELD_OUTSTANDING_QUANTITY'), value: outstanding ?? '-' },
];

/**
 * Link shipments, from a contract row. The shipment-side row carries the contract's
 * quantities but not its material/supplier/facility, so the caller passes those in.
 * `assignedInUnit(unitType)` is the modal's running step-2 total, deducted from outstanding.
 */
export const buildContractLinkDetailFields = (t, row, identity, unitType, assignedInUnit) => {
  if (!row) return [];
  const chosen = rowUnitTypeOf(row, unitType);
  const unit = row.byUnit?.[chosen] ?? {};
  return [
    ...contractIdentityFields(t, identity ?? row, row),
    ...contractQuantityFields(t, row.deliverBy, {
      contracted: unit.contractedQuantity,
      shipped: unit.shippedQuantity,
      outstanding: remainingAfterAssigned(
        unit.outstandingQuantity,
        row.rawByUnit?.[chosen]?.outstandingQuantity,
        unit.unit,
        assignedInUnit?.(chosen) ?? 0,
      ),
    }),
  ];
};

/** Link shipments, from the contract details view model. */
export const buildContractDetailLinkFields = (t, contract, unitType, assignedInUnit) => {
  if (!contract) return [];
  const chosen = chosenUnitType(contract, unitType);
  const quantities = contract[`${chosen}Quantities`] ?? {};
  const raw = contract.rawQuantities?.[chosen];
  return [
    ...contractIdentityFields(t, contract, contract),
    ...contractQuantityFields(t, contract.dispatchBy, {
      contracted: quantities.contracted,
      shipped: quantities.shipped,
      outstanding: remainingAfterAssigned(
        quantities.outstanding,
        raw?.outstanding,
        raw?.unit,
        assignedInUnit?.(chosen) ?? 0,
      ),
    }),
  ];
};

/**
 * Link orders, from the outgoing shipment(s) being linked — Figma 4196:123484 / 4196:122493.
 * A bulk selection is folded to one row first, so both flows read the identical card.
 */
export const buildOutgoingShipmentLinkFields = (t, shipment, unitType) => {
  if (!shipment) return [];
  const unit = rowQuantitiesInUnit(shipment, unitType);
  return [
    { label: t('FIELD_SOLD_TO'), value: shipment.soldTo ?? '-' },
    { label: t('FIELD_SHIPPING_SITES'), value: shipment.shippingTo ?? '-' },
    { label: t('FIELD_SHIPMENT_QUANTITY'), value: unit.shipmentQuantity ?? '-' },
    { label: t('FIELD_ALLOCATED_QUANTITY'), value: unit.allocatedQuantity ?? '-' },
    {
      label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
      value: unit.remainingToAllocateQuantity ?? '-',
    },
  ];
};

/** Link outgoing shipments, from the order(s) being linked — Figma 4196:111461 / 4196:113122. */
export const buildOrderLinkFields = (t, order, unitType, assignedInUnit) => {
  if (!order) return [];
  const chosen = rowUnitTypeOf(order, unitType);
  const unit = order.byUnit?.[chosen] ?? {};
  const raw = order.rawByUnit?.[chosen];
  return [
    { label: t('FIELD_MATERIAL_NAME'), value: order.materialName ?? '-' },
    { label: t('FIELD_SITE'), value: order.facilityName ?? '-' },
    { label: t('FIELD_TERMINAL'), value: order.terminal ?? '-' },
    { label: t('FIELD_SOLD_TO'), value: order.soldTo ?? '-' },
    { label: t('FIELD_SHIPPING_SITES'), value: order.shippingTo ?? '-' },
    { label: t('FIELD_CONTRACTED_QUANTITY'), value: unit.contractedQuantity ?? '-' },
    { label: t('FIELD_SHIPPED_QUANTITY'), value: unit.shippedQuantity ?? '-' },
    {
      // An order's remaining-to-ship IS what a shipment link consumes, so it shrinks live.
      label: t('ORDERS_COL_REMAINING_TO_SHIP'),
      value: remainingAfterAssigned(
        unit.remainingToShipQuantity,
        raw?.remainingToShipQuantity,
        raw?.unit,
        assignedInUnit?.(chosen) ?? 0,
      ),
    },
    { label: t('FIELD_ALLOCATED_QUANTITY'), value: unit.allocatedQuantity ?? '-' },
    {
      label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
      value: unit.remainingToAllocateQuantity ?? '-',
    },
  ];
};
